import './UserMenu.scss'

import { HDSIcon, HDSList, HDSListItem } from '@here/hds-react-components'
import { mapConfig } from 'lib/config'
import { getFaqLocalizedLink } from 'lib/faq'
import { i18n } from 'lib/i18n'
import { createPlatform } from 'map/platform'
import React, { useState } from 'react'
import { getRecentList } from 'utils/storage'

import ConfirmationPrompt from '../../App/components/ConfirmationPrompt'
import { HereAccount } from '../data/reducer'
import { UserMenuHeader } from './UserMenuHeader'
import { UserMenuSignup } from './UserMenuSignup'

interface UserMenuProps {
  user?: HereAccount
  imageUrl?: string
  onClick: () => void
  onSignUp: () => void
  onSignIn: () => void
  onSignOut: () => void
  onFeedback: () => void
  onClearHistory: () => void
  onUnits: () => void
}

export const UserMenu = React.forwardRef(function UserMenu(
  props: UserMenuProps,
  ref: React.Ref<HTMLDivElement>
): JSX.Element | null {
  const { user, onSignUp, onSignOut, onSignIn, onClick, onFeedback, onUnits, onClearHistory } =
    props
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const mapApiVersion = i18n('account.menu.link.version', [mapConfig.mapsApiVersion])
  const omvVersion = createPlatform().getOMVService().getMapVersion()
  const omvVersionString =
    omvVersion !== undefined && omvVersion !== null
      ? i18n('account.menu.link.map-version', [omvVersion.replace('in', 'omv.').replace('jp', '.')])
      : i18n('account.menu.link.map-version', [''])
  const { privacy, terms, cookie } = getFaqLocalizedLink()

  const enableClearHistory =
    localStorage.getItem('CONSENT') === 'ACCEPTED' && getRecentList()?.length > 0

  return (
    <div className="UserMenu__menuOverlay" ref={ref}>
      <section className="UserMenu__top_section">
        {user ? (
          <UserMenuHeader imageUrl={props.imageUrl} user={user} />
        ) : (
          <UserMenuSignup onSignUp={onSignUp} />
        )}
      </section>
      <hr className="UserMenu__divider" />
      <HDSList variant="subtle" size="medium">
        <HDSListItem
          iconCategory="map-view"
          icon="distance"
          onClick={onUnits}
          data-testid="feedback-list-item-units"
        >
          {i18n('account.settings.units.title')}
        </HDSListItem>
        <HDSListItem icon="help">
          <a
            target="_blank"
            href="https://here.freshdesk.com/en/support/solutions?category=web"
            rel="noreferrer"
            onClick={onClick}
            data-testid="feedback-list-item-faq"
          >
            {i18n('account.menu.link.faq')}
          </a>
        </HDSListItem>
        <HDSListItem icon="comment" onClick={onFeedback} data-testid="feedback-list-item">
          {i18n('account.menu.link.feedback')}
        </HDSListItem>
        {enableClearHistory && (
          <HDSListItem
            icon="delete"
            iconCategory="core-ui"
            onClick={() => enableClearHistory && setShowDeleteConfirmation(true)}
            data-testid="feedback-list-item-clear-history"
          >
            {i18n('account.menu.link.clear-history')}
          </HDSListItem>
        )}
        {user ? (
          <HDSListItem
            icon="sign-out"
            onClick={onSignOut}
            data-testid="feedback-list-item-sign-out"
          >
            {i18n('account.menu.link.sign-out')}
          </HDSListItem>
        ) : (
          <HDSListItem icon="sign-in" onClick={onSignIn} data-testid="feedback-list-item-sign-in">
            {i18n('account.menu.link.sign-in')}
          </HDSListItem>
        )}
      </HDSList>
      <hr className="UserMenu__divider" />
      <HDSList variant="subtle" size="auto">
        <HDSListItem size="auto">
          <a
            target="_blank"
            href="https://developer.here.com/develop/javascript-api"
            rel="noreferrer"
            onClick={onClick}
            data-testid="feedback-list-item-maps-api-js"
            className="UserMenu__ApiPromo_ListItem"
          >
            <div className="UserMenu__ApiPromo_container">
              <span className="UserMenu__ApiPromo_text">
                {i18n('account.menu.link.built-with-maps-api')}
              </span>
              <HDSIcon
                name="external-link"
                category="core-ui"
                size="16px"
                className="UserMenu__icon_action"
              />
            </div>
            <div className="UserMenu__ApiPromo_subtitle">{mapApiVersion}</div>
            <div className="UserMenu__ApiPromo_subtitle UserMenu__ApiPromo_subtitle__marginb">
              {omvVersionString}
            </div>
          </a>
        </HDSListItem>
      </HDSList>

      <hr className="UserMenu__divider" />
      <HDSList variant="subtle" size="medium">
        <HDSListItem>
          <a
            target="_blank"
            href={terms}
            rel="noreferrer"
            onClick={onClick}
            data-testid="feedback-list-item-service-terms"
          >
            {i18n('account.menu.link.service-terms')}
          </a>
        </HDSListItem>
        <HDSListItem>
          <a
            target="_blank"
            href={privacy}
            rel="noreferrer"
            onClick={onClick}
            data-testid="feedback-list-item-privacy"
          >
            {i18n('account.menu.link.privacy-policy')}
          </a>
        </HDSListItem>
        <HDSListItem>
          <a
            target="_blank"
            href={cookie}
            rel="noreferrer"
            onClick={onClick}
            data-testid="feedback-list-item-cookie-policy"
          >
            {i18n('account.menu.link.cookie-policy')}
          </a>
        </HDSListItem>
      </HDSList>

      {showDeleteConfirmation && (
        <ConfirmationPrompt
          modalTitle={i18n('account.menu.delete-prompt.title')}
          modalContent={i18n('account.menu.delete-prompt.content')}
          confirmBtnText={i18n('account.menu.delete-prompt.primary-btn')}
          cancelBtnText={i18n('account.menu.delete-prompt.secondary-btn')}
          confirmBtnVariant="danger"
          onConfirm={() => {
            setShowDeleteConfirmation(false)
            onClearHistory()
          }}
          onCancel={() => setShowDeleteConfirmation(false)}
        />
      )}
    </div>
  )
})
